import { supabase } from '../lib/supabase';
import { 
  LoginCredentials, 
  RegisterCredentials, 
  AuthResponse, 
  User, 
  ApiResponse 
} from '../types/auth';

// Función simple de hash para desarrollo
const simpleHash = (password: string): string => {
  return btoa(password + '_salt');
};

const simpleCompare = (password: string, hash: string): boolean => {
  return simpleHash(password) === hash;
};

export const supabaseAuthService = {
  // Registro de usuario
  async register(credentials: RegisterCredentials): Promise<ApiResponse<AuthResponse>> {
    try {
      // Usar autenticación nativa de Supabase
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
          data: {
            name: credentials.name,
            wallet_address: credentials.walletAddress,
            seed_phrase: credentials.seedPhrase
          }
        }
      });

      if (authError) {
        if (authError.message.includes('already registered')) {
          return {
            success: false,
            error: 'El email ya está registrado',
            message: 'El email ya está registrado'
          };
        }
        throw authError;
      }

      if (!authData.user) {
        return {
          success: false,
          error: 'Error en el registro',
          message: 'Error en el registro'
        };
      }

      // Insertar datos adicionales en la tabla users
      const { data: user, error: userError } = await supabase
        .from('users')
        .insert({
          id: authData.user.id,
          email: credentials.email,
          name: credentials.name,
          wallet_address: credentials.walletAddress,
          seed_phrase: credentials.seedPhrase
        })
        .select()
        .single();

      if (userError) {
        console.error('Error inserting user data:', userError);
      }

      return {
        success: true,
        data: {
          user: {
            id: authData.user.id,
            email: credentials.email,
            name: credentials.name,
            walletAddress: credentials.walletAddress,
            seedPhrase: credentials.seedPhrase,
            createdAt: authData.user.created_at,
            updatedAt: authData.user.updated_at || authData.user.created_at
          },
          token: authData.session?.access_token || '',
          message: 'Usuario registrado exitosamente'
        },
        message: 'Usuario registrado exitosamente'
      };
    } catch (error: any) {
      console.error('Error en registro:', error);
      return {
        success: false,
        error: 'Error en el registro',
        message: 'Error en el registro'
      };
    }
  },

  // Login de usuario
  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    try {
      // Usar autenticación nativa de Supabase
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password
      });

      if (authError) {
        return {
          success: false,
          error: 'Credenciales inválidas',
          message: 'Credenciales inválidas'
        };
      }

      if (!authData.user) {
        return {
          success: false,
          error: 'Credenciales inválidas',
          message: 'Credenciales inválidas'
        };
      }

      // Obtener datos adicionales de la tabla users
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authData.user.id)
        .single();

      if (userError) {
        console.error('Error getting user data:', userError);
      }

      return {
        success: true,
        data: {
          user: {
            id: authData.user.id,
            email: credentials.email,
            name: userData?.name || '',
            walletAddress: userData?.wallet_address || '',
            seedPhrase: userData?.seed_phrase || '',
            createdAt: authData.user.created_at,
            updatedAt: authData.user.updated_at || authData.user.created_at
          },
          token: authData.session?.access_token || '',
          message: 'Login exitoso'
        },
        message: 'Login exitoso'
      };
    } catch (error: any) {
      console.error('Error en login:', error);
      return {
        success: false,
        error: 'Error en el login',
        message: 'Error en el login'
      };
    }
  },

  // Logout
  async logout(): Promise<void> {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error en logout:', error);
    }
  },

  // Verificar token y obtener usuario actual
  async getCurrentUser(): Promise<ApiResponse<User>> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return {
          success: false,
          error: 'No hay token de autenticación',
          message: 'No hay token de autenticación'
        };
      }

      // Decodificar token (simulado)
      const payload = JSON.parse(atob(token));
      if (payload.exp < Date.now()) {
        return {
          success: false,
          error: 'Token expirado',
          message: 'Token expirado'
        };
      }

      // Obtener usuario de la base de datos
      const { data: user, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', payload.userId)
        .single();

      if (error || !user) {
        return {
          success: false,
          error: 'Usuario no encontrado',
          message: 'Usuario no encontrado'
        };
      }

      return {
        success: true,
        data: {
          id: user.id,
          email: user.email,
          name: user.name,
          walletAddress: user.wallet_address,
          seedPhrase: user.seed_phrase,
          createdAt: user.created_at,
          updatedAt: user.updated_at
        },
        message: 'Usuario obtenido exitosamente'
      };
    } catch (error: any) {
      console.error('Error al obtener usuario:', error);
      return {
        success: false,
        error: 'Error al obtener usuario',
        message: 'Error al obtener usuario'
      };
    }
  },

  // Verificar si el usuario está autenticado
  async isAuthenticated(): Promise<boolean> {
    const { data: { session } } = await supabase.auth.getSession();
    return !!session;
  },

  // Obtener usuario del localStorage
  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        return null;
      }
    }
    return null;
  },

  // Generar wallet address (simulado)
  generateWalletAddress(): string {
    return '0x' + Math.random().toString(16).substr(2, 40);
  },

  // Generar seed phrase (simulado)
  generateSeedPhrase(): string {
    const words = [
      'bitcoin', 'ethereum', 'wallet', 'secure', 'private', 'key', 
      'crypto', 'blockchain', 'digital', 'asset', 'money', 'future',
      'trust', 'safe', 'guard', 'protect', 'shield', 'vault', 'lock',
      'secure', 'private', 'hidden', 'secret', 'mystery'
    ];
    return Array.from({length: 12}, () => 
      words[Math.floor(Math.random() * words.length)]
    ).join(' ');
  }
}; 